import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Upload, Trash2, Copy, Check, Loader2, X, ImageIcon } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const fmtSize = (b) => b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`;
const assetUrl = (a) => a.url.startsWith('http') ? a.url : `${BACKEND}${a.url}`;

export function MediaGrid({ headers, onSelect, selectable = false }) {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/media`, { headers });
      setAssets(res.data);
    } catch (err) { console.error(err); } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        await axios.post(`${API}/admin/media/upload`, fd, { headers });
      }
      load();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this image from the library?')) return;
    try {
      await axios.delete(`${API}/admin/media/${id}`, { headers });
      setAssets(a => a.filter(x => x.id !== id));
    } catch (err) { console.error(err); }
  };

  const copyUrl = (a) => {
    navigator.clipboard.writeText(assetUrl(a));
    setCopiedId(a.id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  return (
    <div data-testid="media-grid">
      <div className="flex items-center justify-between mb-5">
        <p className="text-white/30 font-manrope text-xs">{assets.length} image{assets.length !== 1 ? 's' : ''} · uploads are automatically optimized for the web</p>
        <label className="flex items-center gap-2 bg-[#D4AF37] text-black font-manrope font-bold text-xs uppercase tracking-widest px-4 py-2.5 rounded-sm hover:bg-[#C5A059] transition-colors cursor-pointer" data-testid="media-upload-btn">
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          {uploading ? 'Uploading...' : 'Upload Images'}
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={upload} />
        </label>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 size={22} className="text-[#D4AF37] animate-spin" /></div>
      ) : assets.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-white/10 rounded-sm" data-testid="media-empty">
          <ImageIcon size={28} className="text-white/15 mx-auto mb-3" />
          <p className="text-white/30 font-manrope text-sm">No images yet — upload your first image to the library</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {assets.map(a => (
            <div key={a.id} className={`group relative bg-[#16161a] border border-white/5 rounded-sm overflow-hidden ${selectable ? 'cursor-pointer hover:border-[#D4AF37]/60' : ''}`}
              onClick={() => selectable && onSelect?.(assetUrl(a))}
              data-testid={`media-asset-${a.id}`}>
              <div className="aspect-square">
                <img src={assetUrl(a)} alt={a.filename} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="p-2">
                <p className="text-white/50 font-manrope text-[10px] truncate">{a.filename}</p>
                <p className="text-white/25 font-manrope text-[9px]">{fmtSize(a.size)}{a.original_size > a.size ? ` (saved ${Math.round((1 - a.size / a.original_size) * 100)}%)` : ''}</p>
              </div>
              {!selectable && (
                <div className="absolute top-2 right-2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={(e) => { e.stopPropagation(); copyUrl(a); }} className="w-7 h-7 bg-black/80 backdrop-blur-sm rounded-sm flex items-center justify-center text-white/60 hover:text-[#D4AF37]" title="Copy URL" data-testid={`copy-media-${a.id}`}>
                    {copiedId === a.id ? <Check size={12} className="text-green-500" /> : <Copy size={12} />}
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); remove(a.id); }} className="w-7 h-7 bg-black/80 backdrop-blur-sm rounded-sm flex items-center justify-center text-white/60 hover:text-red-400" title="Delete" data-testid={`delete-media-${a.id}`}>
                    <Trash2 size={12} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function MediaPickerModal({ headers, onSelect, onClose }) {
  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-6 bg-black/80 backdrop-blur-sm" data-testid="media-picker-modal">
      <div className="bg-[#111113] border border-[#D4AF37]/30 rounded-sm max-w-3xl w-full p-7 relative max-h-[85vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-4 right-4 text-white/40 hover:text-white" data-testid="close-media-picker"><X size={18} /></button>
        <h3 className="font-playfair text-xl text-white mb-5">Choose from Media Library</h3>
        <MediaGrid headers={headers} selectable onSelect={onSelect} />
      </div>
    </div>
  );
}

export function MediaLibraryTab({ headers }) {
  return (
    <div data-testid="media-library-tab">
      <div className="mb-6">
        <h3 className="font-playfair text-xl text-white mb-1">Media Library</h3>
        <p className="text-white/30 font-manrope text-xs">Upload and manage website images. Copy an image URL to use it anywhere, or pick from the library inside the Website Editor.</p>
      </div>
      <MediaGrid headers={headers} />
    </div>
  );
}
